'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { PricingSummary } from './PricingSummary';
import { BillingCycleToggle } from './BillingCycleToggle';
import { CouponInput } from './CouponInput';
import {
    PaymentMethodsSection,
    CardData,
    WalletBillingInfo,
} from './PaymentMethodsSection';
import { UpgradeSuccessModal } from './UpgradeSuccessModal';
import { useChargebee } from '../../hooks/useChargebee';
import { usePaymentIntent } from '../../hooks/usePaymentIntent';
import { useCouponMap } from '../../hooks/useCouponMap';
import {
    billingApi,
    CouponValidationResponse,
    PlanData,
} from '../../services/billingApi';
import { CouponData, CouponProvider } from '../../types/campaign';
import { detectProvider } from '../../services/couponService';
import logger from '../../utils/logger';
export interface BillingFormProps {
    // Plan info
    planId: string;
    planName: string;
    initialIsYearly?: boolean; // If not provided, derived from accountType

    // Pricing
    baseMonthlyPrice: number;
    annualDiscountPct?: number;
    currency: string;

    // Trial info
    trialDays: number;

    // Coupon (optional)
    initialCouponCode?: string;
    initialCouponData?: CouponData | null; // Full coupon data for gap-filling system

    // Plan type for coupon matching (e.g., 'plus', 'pro', 'basic')
    userPlanType?: string;

    // User info (optional, for pre-filling)
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;

    // Account type (for showing relevant features in success modal)
    accountType?: 'personal' | 'business';

    // Callbacks
    onSuccess: (planDisplayName?: string) => void;
    onClose: () => void;

    // Called when Google Pay SDK hangs to verify if payment actually succeeded
    // Parent should check subscription status with backend and return true if payment succeeded
    onVerifySubscription?: () => Promise<boolean>;

    // Theme
    isDarkMode?: boolean;

    // Tracking callbacks for A/B test experiment
    onPaymentFailed?: (error: string, paymentMethod?: string) => void;
    onCouponApplied?: (couponCode: string) => void;
    onCouponFailed?: (couponCode: string, error: string) => void;
    onPaymentMethodSelected?: (
        method: 'card' | 'google_pay' | 'apple_pay'
    ) => void;
}

interface FormState {
    // Coupon state
    couponCode: string;
    couponApplied: boolean;
    appliedCouponId: string;
    couponReason: string;
    priceCoupon: number | null;
    priceCouponFull: number | null;
    trialExtendedViaCoupon: boolean;
    extendedTrialDays: number | null;
    originalTrialDays: number | null; // Backup of trial days before coupon extends them
    couponValidating: boolean;
    couponError: string | null;

    // Form state
    firstName: string;
    lastName: string;
    email: string;
    company: string;

    // UI state
    isSubmitting: boolean;
    error: string | null;
}

/**
 * Native React billing form with Chargebee integration
 * Replaces the iframe-based billing form for better performance and UX
 */
export function BillingForm({
    planId,
    planName,
    initialIsYearly,
    baseMonthlyPrice,
    annualDiscountPct = 50,
    currency,
    trialDays,
    initialCouponCode,
    initialCouponData = null,
    userPlanType,
    userFirstName = '',
    userLastName = '',
    userEmail = '',
    accountType = 'personal',
    onSuccess,
    onClose,
    onVerifySubscription,
    isDarkMode = true,
    onPaymentFailed,
    onCouponApplied,
    onCouponFailed,
    onPaymentMethodSelected,
}: BillingFormProps) {
    // Billing cycle state - default based on account type if not explicitly provided
    // Personal accounts default to monthly, business accounts default to annual
    const [isYearly, setIsYearly] = useState(
        initialIsYearly ?? accountType === 'business'
    );
    // Form state
    const [state, setState] = useState<FormState>({
        couponCode: initialCouponCode || '',
        couponApplied: false,
        appliedCouponId: '',
        couponReason: '',
        priceCoupon: null,
        priceCouponFull: null,
        trialExtendedViaCoupon: false,
        extendedTrialDays: null,
        originalTrialDays: null,
        couponValidating: false,
        couponError: null,
        firstName: userFirstName,
        lastName: userLastName,
        email: userEmail,
        company: '',
        isSubmitting: false,
        error: null,
    });

    // Calculate prices
    const baseAnnualPrice = baseMonthlyPrice * 12;
    const annualTotalPrice = baseAnnualPrice * (1 - annualDiscountPct / 100);
    const annualMonthlyPrice = annualTotalPrice / 12;

    const currentMonthlyPrice = isYearly
        ? annualMonthlyPrice
        : baseMonthlyPrice;
    const currentTotalPrice = isYearly ? annualTotalPrice : baseMonthlyPrice;

    // Final price after coupon
    const finalTotalPrice =
        state.couponApplied && state.priceCouponFull !== null
            ? state.priceCouponFull
            : currentTotalPrice;

    const isFreeWithCoupon = finalTotalPrice === 0;
    const hasTrial = trialDays > 0 || state.trialExtendedViaCoupon;

    // Chargebee SDK
    const {
        isReady: chargebeeReady,
        isLoading: chargebeeLoading,
        loadGooglePay,
        loadApplePay,
        isAppleDevice,
    } = useChargebee();

    // Coupon map for gap-filling system
    // Detects payment provider and builds a map of available coupons
    const provider: CouponProvider = detectProvider();
    const {
        getCouponForSlot,
        setUserCoupon,
        isLoading: couponMapLoading,
    } = useCouponMap(provider, initialCouponData);

    // Track if user manually modified the coupon for the current cycle
    // This prevents auto-applying default coupons when user has made a choice
    const [userModifiedCoupon, setUserModifiedCoupon] = useState(false);

    // Track if user is using card payment (to show user info fields)
    const [isCardPaymentActive, setIsCardPaymentActive] = useState(false);
    // Track the current payment method for analytics
    const [currentPaymentMethod, setCurrentPaymentMethod] = useState<
        'card' | 'google_pay' | 'apple_pay' | null
    >(null);

    // Track if personal details section is expanded
    // Collapsed by default if user info is pre-filled
    const hasPrefilledUserInfo = !!(userFirstName && userEmail);
    const [isPersonalDetailsExpanded, setIsPersonalDetailsExpanded] = useState(
        !hasPrefilledUserInfo
    );

    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successPlanData, setSuccessPlanData] = useState<PlanData | null>(
        null
    );

    // Payment handlers
    const [googlePayHandler, setGooglePayHandler] = useState<unknown | null>(
        null
    );
    const [applePayHandler, setApplePayHandler] = useState<unknown | null>(
        null
    );

    // Sync form state with user prop changes (handles logout/login scenarios)
    // This prevents stale user data from persisting when a different user opens the form
    useEffect(() => {
        setState((prev) => ({
            ...prev,
            firstName: userFirstName,
            lastName: userLastName,
            email: userEmail,
        }));
    }, [userFirstName, userLastName, userEmail]);
    const [walletsLoading, setWalletsLoading] = useState(true);
    // Track if all wallet buttons are actually mounted (web app $q.all pattern)
    const [walletButtonsReady, setWalletButtonsReady] = useState(false);
    // Track if we're waiting for initial coupon validation (to delay wallet init)
    const [initialCouponPending, setInitialCouponPending] = useState(
        !!initialCouponCode
    );

    // Payment intents
    const {
        intents,
        createGooglePayIntent,
        createApplePayIntent,
        recreateIntents,
        recreationKey,
    } = usePaymentIntent({
        amount: finalTotalPrice, // Backend converts to cents
        currencyCode: currency,
        planId,
        enabled: !isFreeWithCoupon && chargebeeReady,
    });

    // Initialize digital wallets when Chargebee is ready
    // Following web app pattern: create payment intent FIRST, then load handler
    // IMPORTANT: Wait for initial coupon validation to complete before initializing wallets
    // This prevents double-mounting when a coupon extends trial days
    useEffect(() => {
        if (initialCouponPending) {
            // Keep loading state while waiting for initial coupon validation
            logger.debug(
                '[BILLING-FORM] Waiting for initial coupon validation before initializing wallets'
            );
            return;
        }

        if (!chargebeeReady || isFreeWithCoupon) {
            setWalletsLoading(false);
            return;
        }

        const initWallets = async () => {
            // Clear existing handlers first to ensure clean re-initialization
            // This prevents stale handler issues when recreationKey changes (billing cycle toggle)
            setGooglePayHandler(null);
            setApplePayHandler(null);

            setWalletsLoading(true);
            logger.debug(
                '[BILLING-FORM] Initializing wallets in parallel (web app pattern)...'
            );
            logger.debug('[BILLING-FORM] isAppleDevice:', isAppleDevice);

            // Web app pattern: Both wallets are enabled unconditionally
            // - googlePaySupported = true (always)
            // - applePaySupported = true (on Apple devices)
            const walletPromises: Promise<void>[] = [];

            // Google Pay initialization (always attempted)
            const initGooglePay = async () => {
                try {
                    logger.debug('[BILLING-FORM] Initializing Google Pay...');
                    // Step 1: Create payment intent FIRST (web app pattern)
                    await createGooglePayIntent();

                    // Step 2: Then load the handler
                    const handler = await loadGooglePay();
                    logger.debug(
                        '[BILLING-FORM] Google Pay handler loaded:',
                        handler
                    );
                    logger.debug(
                        '[BILLING-FORM] Google Pay handler methods:',
                        handler ? Object.keys(handler) : 'null'
                    );
                    setGooglePayHandler(handler);
                } catch (err) {
                    console.error(
                        '[BILLING-FORM] Google Pay init failed:',
                        err
                    );
                    // Don't throw - let other wallets continue (web app does this)
                }
            };

            // Apple Pay initialization (only on Apple devices)
            const initApplePay = async () => {
                if (!isAppleDevice) {
                    logger.debug(
                        '[BILLING-FORM] Not an Apple device, skipping Apple Pay'
                    );
                    return;
                }
                try {
                    logger.debug('[BILLING-FORM] Initializing Apple Pay...');
                    // Step 1: Create payment intent FIRST (web app pattern)
                    await createApplePayIntent();

                    // Step 2: Then load the handler
                    const handler = await loadApplePay();
                    logger.debug(
                        '[BILLING-FORM] Apple Pay handler loaded:',
                        handler
                    );
                    logger.debug(
                        '[BILLING-FORM] Apple Pay handler methods:',
                        handler ? Object.keys(handler) : 'null'
                    );
                    setApplePayHandler(handler);
                } catch (err) {
                    console.error('[BILLING-FORM] Apple Pay init failed:', err);
                    // Don't throw - let other wallets continue (web app does this)
                }
            };

            // Initialize both in parallel using Promise.all (like web app's $q.all)
            walletPromises.push(initGooglePay());
            walletPromises.push(initApplePay());

            try {
                await Promise.all(walletPromises);
                logger.debug(
                    '[BILLING-FORM] All wallet initializations completed'
                );
            } catch (err) {
                // This shouldn't happen as individual errors are caught above
                console.error(
                    '[BILLING-FORM] Some wallet initialization failed:',
                    err
                );
            } finally {
                // Always set loading to false - show what we have (web app pattern)
                setWalletsLoading(false);
                logger.debug(
                    '[BILLING-FORM] Wallet loading complete, paymentMethodsReady'
                );
            }
        };

        initWallets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        chargebeeReady,
        isFreeWithCoupon,
        isAppleDevice,
        loadGooglePay,
        loadApplePay,
        createGooglePayIntent,
        createApplePayIntent,
        initialCouponPending,
        recreationKey,
    ]);

    // Auto-validate initial coupon (only once)
    // Note: handleApplyCoupon is defined later, but we use a ref to ensure single execution
    const initialCouponValidatedRef = useRef(false);

    // Recreate payment intents when coupon changes trial days (apply or remove)
    // This runs AFTER state update, ensuring wallets get the updated trialDays prop
    // NOTE: Skip if wallets haven't been initialized yet (initial coupon case is handled by delaying wallet init)
    const prevCouponAppliedRef = useRef(state.couponApplied);
    const prevExtendedTrialDaysRef = useRef(state.extendedTrialDays);
    const walletsInitializedRef = useRef(false);

    // Track when wallets are first initialized
    useEffect(() => {
        if (!walletsLoading && (googlePayHandler || applePayHandler)) {
            walletsInitializedRef.current = true;
        }
    }, [walletsLoading, googlePayHandler, applePayHandler]);

    useEffect(() => {
        const couponJustApplied =
            state.couponApplied && !prevCouponAppliedRef.current;
        const couponJustRemoved =
            !state.couponApplied && prevCouponAppliedRef.current;
        const trialDaysChanged =
            state.extendedTrialDays !== prevExtendedTrialDaysRef.current;

        // Only recreate intents if wallets have been initialized (not during initial load)
        // During initial load with coupon, we wait for coupon validation before initializing wallets
        if (
            (couponJustApplied ||
                couponJustRemoved ||
                (state.couponApplied && trialDaysChanged)) &&
            walletsInitializedRef.current
        ) {
            logger.debug(
                '[BILLING-FORM] Coupon state changed, recreating payment intents:',
                {
                    couponApplied: state.couponApplied,
                    couponJustApplied,
                    couponJustRemoved,
                    extendedTrialDays: state.extendedTrialDays,
                    effectiveTrialDays:
                        state.trialExtendedViaCoupon && state.extendedTrialDays
                            ? state.extendedTrialDays
                            : trialDays,
                }
            );
            recreateIntents();
        }

        prevCouponAppliedRef.current = state.couponApplied;
        prevExtendedTrialDaysRef.current = state.extendedTrialDays;
    }, [
        state.couponApplied,
        state.extendedTrialDays,
        state.trialExtendedViaCoupon,
        trialDays,
        recreateIntents,
    ]);

    // Handle coupon validation
    const handleApplyCoupon = useCallback(
        async (couponCode: string) => {
            setState((prev) => ({
                ...prev,
                couponValidating: true,
                couponError: null,
            }));

            try {
                logger.debug('[BILLING-FORM] Validating coupon:', couponCode);
                const response: CouponValidationResponse =
                    await billingApi.validateCoupon(couponCode, planId);

                logger.debug(
                    '[BILLING-FORM] Coupon validation response:',
                    response
                );

                // Check for trial extension
                const extendTrial = response.coupon_meta_data?.extend_trial;
                const extendedTrialDays = extendTrial
                    ? parseInt(extendTrial, 10)
                    : null;

                // Get coupon reason with fallback chain (like AngularJS web app)
                const couponReason =
                    response.coupon_meta_data?.reason ||
                    response.coupon_display_text ||
                    'Discount applied';

                // Calculate base prices (like AngularJS web app)
                const annualTotalPrice =
                    baseMonthlyPrice * 12 * (1 - annualDiscountPct / 100);
                const annualMonthlyPrice = annualTotalPrice / 12;
                const baseTotal = isYearly
                    ? annualTotalPrice
                    : baseMonthlyPrice;
                const baseMonthly = isYearly
                    ? annualMonthlyPrice
                    : baseMonthlyPrice;

                // Calculate coupon prices
                const couponTotal = response.total;
                const couponMonthly = isYearly ? couponTotal / 12 : couponTotal;

                // Only set coupon prices if the coupon actually changes the price
                // (like AngularJS showCoupon logic)
                const hasPriceDiscount =
                    (couponTotal !== null &&
                        couponTotal !== undefined &&
                        couponTotal !== baseTotal) ||
                    (couponMonthly !== null &&
                        couponMonthly !== undefined &&
                        couponMonthly !== baseMonthly);

                setState((prev) => ({
                    ...prev,
                    couponApplied: true,
                    appliedCouponId: couponCode,
                    couponReason: hasPriceDiscount ? couponReason : '', // Only show reason if there's a price discount
                    priceCoupon: hasPriceDiscount ? couponMonthly : null,
                    priceCouponFull: hasPriceDiscount ? couponTotal : null,
                    trialExtendedViaCoupon: !!extendedTrialDays,
                    extendedTrialDays,
                    // Backup original trial days if extending (like AngularJS oldTrialDays)
                    originalTrialDays:
                        extendedTrialDays && !prev.originalTrialDays
                            ? trialDays
                            : prev.originalTrialDays,
                    couponValidating: false,
                    couponError: null,
                }));

                logger.debug('[BILLING-FORM] Coupon applied:', {
                    couponCode,
                    total: couponTotal,
                    monthly: couponMonthly,
                    extendTrial,
                    extendedTrialDays,
                    trialExtendedViaCoupon: !!extendedTrialDays,
                    couponReason,
                    hasPriceDiscount,
                    rawMetaData: response.coupon_meta_data,
                });

                // Track coupon applied for A/B test experiment
                onCouponApplied?.(couponCode);

                // Clear initial coupon pending flag - wallet init can now proceed
                setInitialCouponPending(false);
            } catch (err) {
                console.error('[BILLING-FORM] Coupon validation failed:', err);
                const errorMessage =
                    err instanceof Error ? err.message : 'Invalid coupon code';
                setState((prev) => ({
                    ...prev,
                    couponValidating: false,
                    couponError: errorMessage,
                }));
                // Track coupon failure for A/B test experiment
                onCouponFailed?.(couponCode, errorMessage);
                // Clear initial coupon pending flag even on error - wallet init can proceed
                setInitialCouponPending(false);
            }
        },
        [
            planId,
            isYearly,
            trialDays,
            baseMonthlyPrice,
            annualDiscountPct,
            onCouponApplied,
            onCouponFailed,
        ]
    );

    // Auto-validate initial coupon (effect placed after handleApplyCoupon is defined)
    useEffect(() => {
        if (initialCouponCode && !initialCouponValidatedRef.current) {
            initialCouponValidatedRef.current = true;
            handleApplyCoupon(initialCouponCode);
        }
    }, [initialCouponCode, handleApplyCoupon]);

    // Apply coupon from the coupon map on initial load (if no initialCouponCode)
    // This ensures default coupons are applied when user arrives without a specific coupon
    const initialMapCouponAppliedRef = useRef(false);
    useEffect(() => {
        // Skip if we already have an initial coupon code being validated
        if (initialCouponCode || initialMapCouponAppliedRef.current) {
            return;
        }
        // Skip if coupon map is still loading
        if (couponMapLoading) {
            return;
        }
        // Skip if userPlanType is not available for matching
        if (!userPlanType) {
            return;
        }

        initialMapCouponAppliedRef.current = true;

        const currentCycle = isYearly ? 'annually' : 'monthly';
        const couponEntry = getCouponForSlot(
            accountType,
            userPlanType,
            currentCycle
        );

        if (couponEntry?.coupon?.coupon_code) {
            logger.debug(
                '[BILLING-FORM] Applying initial coupon from map:',
                couponEntry.coupon.coupon_code
            );
            handleApplyCoupon(couponEntry.coupon.coupon_code);
        }
    }, [
        initialCouponCode,
        couponMapLoading,
        userPlanType,
        isYearly,
        accountType,
        getCouponForSlot,
        handleApplyCoupon,
    ]);

    // Handle coupon removal (like AngularJS removeCoupon)
    const handleRemoveCoupon = useCallback(() => {
        logger.debug(
            '[BILLING-FORM] Removing coupon, restoring original trial days'
        );
        setState((prev) => ({
            ...prev,
            couponApplied: false,
            appliedCouponId: '',
            couponReason: '',
            priceCoupon: null,
            priceCouponFull: null,
            trialExtendedViaCoupon: false,
            extendedTrialDays: null,
            // Clear the backup since we're restoring
            originalTrialDays: null,
            couponError: null,
        }));
        // Note: Payment intent recreation is handled by useEffect watching state.couponApplied
    }, []);

    // Handle billing cycle toggle change
    const handleBillingCycleChange = useCallback(
        async (newIsYearly: boolean) => {
            const newCycle = newIsYearly ? 'annually' : 'monthly';
            logger.debug('[BILLING-FORM] Billing cycle changed:', {
                from: isYearly ? 'yearly' : 'monthly',
                to: newCycle,
                userPlanType,
                accountType,
            });

            setIsYearly(newIsYearly);

            // Reset the user modified flag when changing cycles
            // This allows default coupons to be applied for the new cycle
            setUserModifiedCoupon(false);

            // Look up coupon for the new cycle from the coupon map
            // Only if userPlanType is available for matching
            if (userPlanType) {
                const couponEntry = getCouponForSlot(
                    accountType,
                    userPlanType,
                    newCycle
                );

                if (couponEntry) {
                    logger.debug(
                        '[BILLING-FORM] Found coupon for new cycle from map:',
                        {
                            couponCode: couponEntry.coupon.coupon_code,
                            source: couponEntry.source,
                        }
                    );
                    // Silently apply the coupon from the map
                    const couponCode = couponEntry.coupon.coupon_code;
                    if (couponCode) {
                        setTimeout(() => {
                            handleApplyCoupon(couponCode);
                        }, 0);
                    }
                } else if (state.couponApplied) {
                    // No coupon in map for this slot - remove current coupon
                    logger.debug(
                        '[BILLING-FORM] No coupon found for new cycle, removing current coupon'
                    );
                    handleRemoveCoupon();
                }
            } else if (state.couponApplied && state.appliedCouponId) {
                // Fallback: re-validate current coupon for new billing cycle
                logger.debug(
                    '[BILLING-FORM] Re-validating coupon for new billing cycle (no userPlanType)'
                );
                setTimeout(() => {
                    handleApplyCoupon(state.appliedCouponId);
                }, 0);
            }

            // Payment intent recreation will be triggered by useEffect watching isYearly
            recreateIntents();
        },
        [
            isYearly,
            state.couponApplied,
            state.appliedCouponId,
            handleApplyCoupon,
            handleRemoveCoupon,
            recreateIntents,
            userPlanType,
            accountType,
            getCouponForSlot,
        ]
    );

    // Handle successful subscription - show celebration modal
    const handleSubscriptionSuccess = useCallback(async () => {
        logger.debug(
            '[BILLING-FORM] Subscription successful, fetching plan data for modal...'
        );
        setState((prev) => ({ ...prev, isSubmitting: false }));

        try {
            // Fetch plan data for the success modal
            const planData = await billingApi.getPlanByProviderId(planId);
            logger.debug('[BILLING-FORM] Plan data for modal:', planData);
            setSuccessPlanData(planData);
        } catch (err) {
            console.error(
                '[BILLING-FORM] Failed to fetch plan data for modal:',
                err
            );
            // Still show modal even if plan data fails
        }

        // Show the success modal
        setShowSuccessModal(true);
    }, [planId]);

    // Handle success modal close - call original onSuccess with plan name
    const handleSuccessModalClose = useCallback(() => {
        setShowSuccessModal(false);
        // Pass the plan display name for optimistic UI update
        onSuccess(successPlanData?.display_name);
        setSuccessPlanData(null);
    }, [onSuccess, successPlanData]);

    // Handle wallet payment success
    const handleWalletSuccess = useCallback(
        async (
            token: string,
            gatewayAccountId: string,
            paymentType: 'google_pay' | 'apple_pay',
            walletBillingInfo?: WalletBillingInfo
        ) => {
            // Track wallet payment method selection for A/B test experiment
            setCurrentPaymentMethod(paymentType);
            onPaymentMethodSelected?.(paymentType);

            setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

            try {
                // Use billing info from wallet if available, otherwise fall back to form state
                const firstName =
                    walletBillingInfo?.firstName ||
                    state.firstName ||
                    userFirstName ||
                    '';
                const lastName =
                    walletBillingInfo?.lastName ||
                    state.lastName ||
                    userLastName ||
                    '';
                const email =
                    walletBillingInfo?.email || state.email || userEmail || '';

                logger.debug('[BILLING-FORM] Submitting wallet payment:', {
                    paymentType,
                    planId,
                    gatewayAccountId,
                    billingInfo: { firstName, lastName, email },
                });

                const response = await billingApi.submitBillingInfoAndSubscribe(
                    {
                        billingInfo: {
                            firstName,
                            lastName,
                            email,
                            company: state.company || undefined,
                        },
                        paymentMethod: {
                            type: paymentType,
                            gatewayToken: token,
                            gatewayAccountId: gatewayAccountId,
                        },
                        planId,
                        couponCode: state.couponApplied
                            ? state.appliedCouponId
                            : undefined,
                    }
                );

                // Debug: log exact response structure
                logger.debug(
                    '[BILLING-FORM] Full wallet response:',
                    JSON.stringify(response)
                );
                logger.debug(
                    '[BILLING-FORM] response.success:',
                    response.success,
                    typeof response.success
                );
                logger.debug(
                    '[BILLING-FORM] response.type:',
                    response.type,
                    typeof response.type
                );
                logger.debug(
                    '[BILLING-FORM] response.message?.type:',
                    response.message?.type,
                    typeof response.message?.type
                );

                // Check for success - handle multiple response formats:
                // Format 1: { success: true }
                // Format 2: { type: 'success' }
                // Format 3: { message: { type: 'success', text: '...' } }
                const isSuccess =
                    response.success === true ||
                    response.type === 'success' ||
                    (typeof response.type === 'string' &&
                        response.type.toLowerCase().trim() === 'success') ||
                    response.message?.type === 'success';

                if (isSuccess) {
                    logger.debug(
                        '[BILLING-FORM] Wallet subscription created successfully:',
                        response
                    );
                    handleSubscriptionSuccess();
                } else {
                    // Extract error message from various response formats
                    const errorMessage =
                        response.error ||
                        (response.message?.type === 'error'
                            ? response.message?.text
                            : null) ||
                        response.message?.text ||
                        'Subscription failed';
                    throw new Error(errorMessage);
                }
            } catch (err) {
                console.error('[BILLING-FORM] Wallet payment failed:', err);
                setState((prev) => ({
                    ...prev,
                    isSubmitting: false,
                    error:
                        err instanceof Error
                            ? err.message
                            : 'Payment failed. Please try again.',
                }));
            }
        },
        [
            planId,
            state.firstName,
            state.lastName,
            state.email,
            state.company,
            state.couponApplied,
            state.appliedCouponId,
            handleSubscriptionSuccess,
            userFirstName,
            userLastName,
            userEmail,
            onPaymentMethodSelected,
        ]
    );

    // Handle card payment
    const handleCardSubmit = useCallback(
        async (cardData: CardData) => {
            setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

            try {
                logger.debug('[BILLING-FORM] Submitting card payment:', {
                    planId,
                });

                const response = await billingApi.submitBillingInfoAndSubscribe(
                    {
                        billingInfo: {
                            firstName: state.firstName,
                            lastName: state.lastName,
                            email: state.email,
                            company: state.company || undefined,
                        },
                        paymentMethod: {
                            type: 'card',
                            cardNumber: cardData.number,
                            expiryMonth: cardData.expiryMonth,
                            expiryYear: cardData.expiryYear,
                            cvv: cardData.cvv,
                        },
                        planId,
                        couponCode: state.couponApplied
                            ? state.appliedCouponId
                            : undefined,
                    }
                );

                // Check for success - handle multiple response formats
                const isSuccess =
                    response.success === true ||
                    response.type === 'success' ||
                    (typeof response.type === 'string' &&
                        response.type.toLowerCase().trim() === 'success') ||
                    response.message?.type === 'success';

                if (isSuccess) {
                    logger.debug(
                        '[BILLING-FORM] Card subscription created successfully:',
                        response
                    );
                    handleSubscriptionSuccess();
                } else {
                    const errorMessage =
                        response.error ||
                        (response.message?.type === 'error'
                            ? response.message?.text
                            : null) ||
                        response.message?.text ||
                        'Subscription failed';
                    throw new Error(errorMessage);
                }
            } catch (err) {
                console.error('[BILLING-FORM] Card payment failed:', err);
                setState((prev) => ({
                    ...prev,
                    isSubmitting: false,
                    error:
                        err instanceof Error
                            ? err.message
                            : 'Payment failed. Please try again.',
                }));
            }
        },
        [
            planId,
            state.firstName,
            state.lastName,
            state.email,
            state.company,
            state.couponApplied,
            state.appliedCouponId,
            handleSubscriptionSuccess,
        ]
    );

    // Handle $0 payment (coupon makes it free)
    const handleFreeSubscribe = useCallback(async () => {
        setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

        try {
            logger.debug('[BILLING-FORM] Submitting free subscription:', {
                planId,
            });

            const response = await billingApi.submitBillingInfoAndSubscribe({
                billingInfo: {
                    firstName: state.firstName,
                    lastName: state.lastName,
                    email: state.email,
                    company: state.company || undefined,
                },
                paymentMethod: {
                    type: 'card', // No payment needed but API requires a type
                },
                planId,
                couponCode: state.appliedCouponId,
            });

            // Check for success - handle multiple response formats
            const isSuccess =
                response.success === true ||
                response.type === 'success' ||
                (typeof response.type === 'string' &&
                    response.type.toLowerCase().trim() === 'success') ||
                response.message?.type === 'success';

            if (isSuccess) {
                logger.debug(
                    '[BILLING-FORM] Free subscription created:',
                    response
                );
                handleSubscriptionSuccess();
            } else {
                const errorMessage =
                    response.error ||
                    (response.message?.type === 'error'
                        ? response.message?.text
                        : null) ||
                    response.message?.text ||
                    'Subscription failed';
                throw new Error(errorMessage);
            }
        } catch (err) {
            console.error('[BILLING-FORM] Free subscription failed:', err);
            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                error:
                    err instanceof Error
                        ? err.message
                        : 'Failed to create subscription.',
            }));
        }
    }, [
        planId,
        state.firstName,
        state.lastName,
        state.email,
        state.company,
        state.appliedCouponId,
        handleSubscriptionSuccess,
    ]);

    const handlePaymentError = useCallback(
        (error: string) => {
            setState((prev) => ({ ...prev, error }));
            // Track payment failure for A/B test experiment
            onPaymentFailed?.(error, currentPaymentMethod || 'card');
        },
        [onPaymentFailed, currentPaymentMethod]
    );

    // Handle Google Pay timeout - verify if payment actually succeeded despite SDK hanging
    // This is needed when browser extensions interfere with Chargebee SDK callbacks
    const handleGooglePayTimeout = useCallback(async (): Promise<boolean> => {
        if (!onVerifySubscription) {
            console.log(
                '[BILLING-FORM] No verification callback provided, cannot verify payment'
            );
            return false;
        }

        try {
            console.log(
                '[BILLING-FORM] Verifying subscription status after Google Pay timeout...'
            );
            const paymentSucceeded = await onVerifySubscription();

            if (paymentSucceeded) {
                console.log(
                    '[BILLING-FORM] Payment verified successful - showing success modal'
                );
                handleSubscriptionSuccess();
                return true;
            } else {
                console.log(
                    '[BILLING-FORM] Payment not verified - subscription not found'
                );
                return false;
            }
        } catch (err) {
            console.error(
                '[BILLING-FORM] Subscription verification failed:',
                err
            );
            return false;
        }
    }, [onVerifySubscription, handleSubscriptionSuccess]);

    // Web app pattern: Called when ALL expected wallet buttons are mounted
    // This mirrors the $q.all(walletPromises) behavior
    const handleWalletsReady = useCallback(() => {
        logger.debug(
            '[BILLING-FORM] All wallet buttons mounted, ready to show payment methods'
        );
        setWalletButtonsReady(true);
    }, []);

    // Check if user info fields should be shown
    // For wallet payments (Google Pay/Apple Pay), user info comes from the wallet
    // For card payments or free subscriptions, we need to collect user info
    const hasWallet = googlePayHandler || applePayHandler;
    const needsUserInfoForPayment =
        isCardPaymentActive || !hasWallet || isFreeWithCoupon;
    // Don't show user info fields while wallets are still loading/mounting
    // Web app pattern: Keep the form clean until we know if wallets are available
    const isWalletsStillLoading =
        (chargebeeLoading || walletsLoading || !walletButtonsReady) &&
        !isFreeWithCoupon;
    // Show user info fields when payment method requires them (card/free) and wallets aren't loading
    // Note: We show fields even if already filled - user should be able to edit them
    const showUserInfoFields =
        needsUserInfoForPayment && !isWalletsStillLoading;
    // Check if required user info is missing (for submit button validation)
    // Note: Last name is optional for credit card payments
    const isUserInfoIncomplete = !state.firstName || !state.email;

    return (
        <div className="space-y-6">
            {/* Plan Header with Logo - Using PNG for faster loading */}
            <div className="flex flex-col items-center">
                <Image
                    src="/niio_logo_white.png"
                    alt="Niio"
                    width={104}
                    height={19}
                    className={`h-5 w-auto mb-2 ${
                        isDarkMode ? 'block' : 'hidden'
                    }`}
                    priority
                    unoptimized
                />
                <Image
                    src="/niio_logo_black.png"
                    alt="Niio"
                    width={104}
                    height={19}
                    className={`h-5 w-auto mb-2 ${
                        isDarkMode ? 'hidden' : 'block'
                    }`}
                    priority
                    unoptimized
                />
                <h2
                    className={`text-xl font-semibold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                >
                    {planName} Plan
                </h2>
            </div>

            {/* Billing Cycle Toggle */}
            <BillingCycleToggle
                isYearly={isYearly}
                onChange={handleBillingCycleChange}
                annualDiscountPct={annualDiscountPct}
                isDarkMode={isDarkMode}
            />

            {/* Pricing Summary */}
            <PricingSummary
                baseMonthlyPrice={baseMonthlyPrice}
                isYearly={isYearly}
                currency={currency}
                trialDays={trialDays}
                originalTrialDays={state.originalTrialDays}
                annualDiscountPct={annualDiscountPct}
                couponApplied={state.couponApplied}
                couponReason={state.couponReason}
                priceCoupon={state.priceCoupon}
                priceCouponFull={state.priceCouponFull}
                trialExtendedViaCoupon={state.trialExtendedViaCoupon}
                extendedTrialDays={state.extendedTrialDays}
                isDarkMode={isDarkMode}
            />

            {/* Coupon Input */}
            <CouponInput
                onApply={handleApplyCoupon}
                onRemove={handleRemoveCoupon}
                appliedCoupon={
                    state.couponApplied ? state.appliedCouponId : null
                }
                couponReason={state.couponReason}
                isValidating={state.couponValidating}
                error={state.couponError}
                isDarkMode={isDarkMode}
            />

            {/* User Info Fields (if needed) - Collapsible when pre-filled */}
            {showUserInfoFields && (
                <div
                    className={`rounded-lg overflow-hidden ${
                        isDarkMode
                            ? 'border border-gray-700'
                            : 'border border-gray-200'
                    }`}
                >
                    {/* Header - clickable to expand/collapse when pre-filled */}
                    <button
                        type="button"
                        onClick={() =>
                            hasPrefilledUserInfo &&
                            setIsPersonalDetailsExpanded(
                                !isPersonalDetailsExpanded
                            )
                        }
                        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                            isDarkMode
                                ? `bg-gray-800 ${
                                      hasPrefilledUserInfo
                                          ? 'cursor-pointer hover:bg-gray-750'
                                          : 'cursor-default'
                                  }`
                                : `bg-gray-50 ${
                                      hasPrefilledUserInfo
                                          ? 'cursor-pointer hover:bg-gray-100'
                                          : 'cursor-default'
                                  }`
                        }`}
                    >
                        <div className="flex items-center space-x-2">
                            <svg
                                className={`w-4 h-4 ${
                                    isDarkMode
                                        ? 'text-gray-400'
                                        : 'text-gray-500'
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                            <span
                                className={`text-sm font-medium ${
                                    isDarkMode
                                        ? 'text-gray-300'
                                        : 'text-gray-700'
                                }`}
                            >
                                {hasPrefilledUserInfo &&
                                !isPersonalDetailsExpanded &&
                                (state.firstName ||
                                    state.lastName ||
                                    state.email)
                                    ? `${[state.firstName, state.lastName]
                                          .filter(Boolean)
                                          .join(' ')}${
                                          state.email ? ` • ${state.email}` : ''
                                      }`
                                    : 'Personal Details'}
                            </span>
                        </div>
                        {hasPrefilledUserInfo && (
                            <svg
                                className={`w-4 h-4 transition-transform ${
                                    isDarkMode
                                        ? 'text-gray-500'
                                        : 'text-gray-400'
                                } ${
                                    isPersonalDetailsExpanded
                                        ? 'rotate-180'
                                        : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        )}
                    </button>

                    {/* Expandable content */}
                    {isPersonalDetailsExpanded && (
                        <div
                            className={`p-4 space-y-3 ${
                                isDarkMode ? 'bg-gray-800/50' : 'bg-white'
                            }`}
                        >
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    id="firstName"
                                    type="text"
                                    value={state.firstName}
                                    onChange={(e) =>
                                        setState((prev) => ({
                                            ...prev,
                                            firstName: e.target.value,
                                        }))
                                    }
                                    placeholder="First name *"
                                    data-ph-no-capture
                                    className={`ph-no-capture w-full px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${
                                        isDarkMode
                                            ? 'border border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                                            : 'border border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                                    }`}
                                    required
                                />
                                <input
                                    id="lastName"
                                    type="text"
                                    value={state.lastName}
                                    onChange={(e) =>
                                        setState((prev) => ({
                                            ...prev,
                                            lastName: e.target.value,
                                        }))
                                    }
                                    placeholder="Last name"
                                    data-ph-no-capture
                                    className={`ph-no-capture w-full px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${
                                        isDarkMode
                                            ? 'border border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                                            : 'border border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                                    }`}
                                />
                            </div>
                            <input
                                id="email"
                                type="email"
                                value={state.email}
                                onChange={(e) =>
                                    setState((prev) => ({
                                        ...prev,
                                        email: e.target.value,
                                    }))
                                }
                                placeholder="Email *"
                                data-ph-no-capture
                                className={`ph-no-capture w-full px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${
                                    isDarkMode
                                        ? 'border border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                                        : 'border border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                                }`}
                                required
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Error Message */}
            {state.error && (
                <div
                    className={`rounded-lg p-4 ${
                        isDarkMode
                            ? 'bg-red-900/20 border border-red-800'
                            : 'bg-red-50 border border-red-200'
                    }`}
                >
                    <p
                        className={`text-sm flex items-center space-x-2 ${
                            isDarkMode ? 'text-red-400' : 'text-red-600'
                        }`}
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span>{state.error}</span>
                    </p>
                </div>
            )}

            {/* Payment Methods or Free Subscribe */}
            {isFreeWithCoupon ? (
                <button
                    onClick={handleFreeSubscribe}
                    disabled={state.isSubmitting || isUserInfoIncomplete}
                    className="w-full py-3 px-4 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {state.isSubmitting ? (
                        <span className="flex items-center justify-center space-x-2">
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span>Creating subscription...</span>
                        </span>
                    ) : (
                        'Start Free Subscription'
                    )}
                </button>
            ) : (
                <PaymentMethodsSection
                    googlePayHandler={googlePayHandler}
                    applePayHandler={applePayHandler}
                    googlePayIntent={intents.googlePay}
                    applePayIntent={intents.applePay}
                    isLoading={chargebeeLoading || walletsLoading}
                    hasTrial={hasTrial}
                    trialDays={
                        state.trialExtendedViaCoupon && state.extendedTrialDays
                            ? state.extendedTrialDays
                            : trialDays
                    }
                    trialEndDate={
                        hasTrial
                            ? new Date(
                                  Date.now() +
                                      (state.trialExtendedViaCoupon &&
                                      state.extendedTrialDays
                                          ? state.extendedTrialDays
                                          : trialDays) *
                                          24 *
                                          60 *
                                          60 *
                                          1000
                              )
                            : null
                    }
                    amount={finalTotalPrice}
                    isAppleDevice={isAppleDevice}
                    isYearly={isYearly}
                    planName={planName}
                    isUserInfoComplete={!!(state.firstName && state.email)}
                    onGooglePaySuccess={(
                        token,
                        gatewayAccountId,
                        billingInfo
                    ) =>
                        handleWalletSuccess(
                            token,
                            gatewayAccountId,
                            'google_pay',
                            billingInfo
                        )
                    }
                    onApplePaySuccess={(token, gatewayAccountId, billingInfo) =>
                        handleWalletSuccess(
                            token,
                            gatewayAccountId,
                            'apple_pay',
                            billingInfo
                        )
                    }
                    onCardSubmit={handleCardSubmit}
                    onError={handlePaymentError}
                    onCardFormToggle={(isActive) => {
                        setIsCardPaymentActive(isActive);
                        if (isActive) {
                            setCurrentPaymentMethod('card');
                            onPaymentMethodSelected?.('card');
                        }
                    }}
                    onWalletsReady={handleWalletsReady}
                    onGooglePayTimeout={handleGooglePayTimeout}
                    isDarkMode={isDarkMode}
                />
            )}

            {/* Submitting Overlay */}
            {state.isSubmitting && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div
                        className={`rounded-lg p-6 flex items-center space-x-4 ${
                            isDarkMode
                                ? 'bg-gray-800 border border-gray-700'
                                : 'bg-white'
                        }`}
                    >
                        <svg
                            className="animate-spin h-8 w-8 text-purple-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <span
                            className={
                                isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }
                        >
                            Processing your subscription...
                        </span>
                    </div>
                </div>
            )}

            {/* Success Celebration Modal */}
            <UpgradeSuccessModal
                isOpen={showSuccessModal}
                onClose={handleSuccessModalClose}
                planName={planName}
                accountType={accountType}
            />
        </div>
    );
}

export default BillingForm;
